import {
  WSButton,
  WSButtons,
  WSContainer,
  WSElement,
  WSFlexBox,
  WSFlexGrid,
  WSGrid,
  WSIcon,
  WSImage,
  WSLoader,
  WSScreen,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { useEffect, useRef } from "react";
import { QueryStatus } from "react-query";
import { useHistory } from "react-router-dom";
import { openIntercom } from "../utils/intercom";
import { useWSQuery } from "./helpers";
import { IS_PRODUCTION_ENV } from "../components/App";

export type WSQueryRenderErrorMessage = (
  errors: any,
  refetch: () => Promise<any>
) => React.ReactNode;

export type WSQueryRenderErrorsMessage = (
  errors: any[],
  refetch: () => Promise<any>
) => React.ReactNode;

export type WSQueryRenderLoader = () => React.ReactNode;

export const RenderDefaultPageErrorMessage: (
  error?: any,
  refetch?: () => any
) => React.ReactNode = (error, refetch) => {
  const history = useHistory();

  return (
    <WSContainer>
      <WSFlexGrid container py="XL" style={{ flexDirection: "revert" }}>
        <WSScreen.Mobile>
          <WSFlexGrid item mobile="12">
            <WSImage
              placeholder={"Opps image :)"}
              src={require("../assets/images/opps-error.png").default}
            />
          </WSFlexGrid>
        </WSScreen.Mobile>
        <WSFlexGrid item mobile="12" tablet="6" desktop="5">
          <WSText.Heading1>
            Oops, something went wrong on our end.{" "}
          </WSText.Heading1>

          <WSText.Paragraph color="grey600" mt="M">
            If this problem persists,{" "}
            <WSButton.Link inline onClick={openIntercom}>
              contact support.
            </WSButton.Link>
          </WSText.Paragraph>
          <WSButtons mt="L">
            <WSButton onClick={() => history.push("/member/dashboard")}>
              Go to Dashboard
            </WSButton>

            {refetch && (
              <WSButton.Secondary onClick={refetch}>Retry</WSButton.Secondary>
            )}
          </WSButtons>
        </WSFlexGrid>

        <WSScreen.Desktop>
          <WSFlexGrid item desktop="2" />
        </WSScreen.Desktop>
        <WSScreen.TabletAndDesktop>
          <WSFlexGrid item tablet="6" desktop="5">
            <WSImage
              placeholder={""}
              src={require("../assets/images/opps-error.png").default}
            />
          </WSFlexGrid>
        </WSScreen.TabletAndDesktop>
      </WSFlexGrid>
    </WSContainer>
  );
};

const renderDefaultErrorMessage: WSQueryRenderErrorMessage = (
  errors,
  refetch
) => {
  return errors[0]?.response?.status === 403 ? (
    <WSFlexBox.Center
      p="XL"
      direction="column"
      wrap="nowrap"
      style={{ height: "100%", flex: "1" }}
      data-testid="serviceError"
    >
      <WSIcon name="warning" size="L" color="yellow500" />
      <WSText.Heading3 color="grey700" mt="M">
        You don't have enough permissions to access this page
      </WSText.Heading3>

      <WSFlexBox.Center pt="L">
        Please
        <WSButton.Link inline onClick={openIntercom}>
          {" "}
          contact our support{" "}
        </WSButton.Link>
        and our team will be happy to help you
      </WSFlexBox.Center>
    </WSFlexBox.Center>
  ) : (
    <WSFlexBox.Center
      p="XL"
      direction="column"
      wrap="nowrap"
      style={{ height: "100%", flex: "1" }}
      data-testid="serviceError"
    >
      <WSIcon name="warning" size="L" color="yellow500" />
      <WSText.Heading3 color="grey700" mt="M">
        We had trouble loading part of this page
      </WSText.Heading3>

      <WSFlexBox.Center pt="XL">
        <WSButton onClick={refetch}>Retry</WSButton>
      </WSFlexBox.Center>

      <WSFlexBox.Center pt="L">
        If this persists, please
        <WSButton.Link inline onClick={openIntercom}>
          {" "}
          contact our support{" "}
        </WSButton.Link>
        and our team will be happy to help you
      </WSFlexBox.Center>
    </WSFlexBox.Center>
  );
};

const renderDefaultErrorsMessage: WSQueryRenderErrorMessage = (
  errors,
  refetch
) => renderDefaultErrorMessage(errors as any, refetch);

type ErrorBoundaryProps = {};

type ErrorBoundaryState = {
  hasError: boolean;
  error?: Error;
  errors?: Error[];
};

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, errors: [error] };
  }

  render() {
    if (this.state.hasError) {
      return (
        <WSContainer
          verticalPadding
          style={{ height: "100%", display: "flex" }}
          data-testid="serviceError"
        >
          <WSGrid.Centered XS={4} S={8} M={6}>
            <WSText.Heading2>Uh-oh! Error! 🤕</WSText.Heading2>
            <WSText mt="M">
              Sorry about that, it appears we've hit a snag. Please reload your
              browser and try again, or{" "}
              <WSButton.Link inline onClick={openIntercom}>
                contact support
              </WSButton.Link>{" "}
              if the problem persists.
            </WSText>
            {!IS_PRODUCTION_ENV && (
              <WSText.Paragraph color="red400" mt="S">
                One or more errors are occurring in this component
                ErrorBoundary:
              </WSText.Paragraph>
            )}
            {!IS_PRODUCTION_ENV &&
              this.state.errors &&
              (this.state.errors as any[]).map(err => {
                console.log("ErrorBoundary", err);
                return (
                  <WSText.Paragraph color="red400" mt="S">
                    {err.message}
                  </WSText.Paragraph>
                );
              })}
          </WSGrid.Centered>
        </WSContainer>
      );
    }

    return this.props.children;
  }
}

export const defaultRenderLoader = () => (
  <WSElement my="XXL">
    <WSLoader.Spinner />
  </WSElement>
);

type Query = ReturnType<typeof useWSQuery>;

export type ChildQuery<T> = T extends { data: infer U }
  ? T & { data: NonNullable<U> }
  : never;

interface PropsQuery<Q extends Query> {
  query: Q;
  renderLoader?: WSQueryRenderLoader;
  renderError?: WSQueryRenderErrorMessage;
  onSuccess?: (props: ChildQuery<Q>) => void;
  children(props: ChildQuery<Q>): React.ReactNode;
  isPage?: boolean;
}

const QueryComponent = <Q extends Query = Query>({
  children,
  renderError = renderDefaultErrorMessage,
  renderLoader = defaultRenderLoader,
  onSuccess,
  query,
  isPage
}: PropsQuery<Q>): any => {
  const isSuccess = query.isSuccess;
  const successRef = useRef(false);

  useEffect(() => {
    if (!successRef.current && isSuccess) {
      successRef.current = true;
      onSuccess?.(query as any);
    }
  }, [isSuccess, onSuccess, query]);

  if (query.isLoading) {
    return renderLoader();
  }

  if (query.isError) {
    if (isPage && renderDefaultErrorMessage === renderError) {
      return RenderDefaultPageErrorMessage(query.error, query.refetch);
    } else {
      return renderError(query.error, query.refetch);
    }
  }

  return children(query as any);
};

export type ChildQueries<T> = {
  [K in keyof T]: T[K] extends { data: infer U }
    ? T[K] & { data: NonNullable<U> }
    : never;
};

type Queries = { [key: string]: Query };

interface PropsQueries<Q extends Queries> {
  queries: Q;
  renderLoader?: WSQueryRenderLoader;
  renderErrors?: WSQueryRenderErrorsMessage;
  onSuccess?: (props: ChildQueries<Q>) => void;
  onError?: (props: ChildQueries<Q>) => void;
  children(props: ChildQueries<Q>): React.ReactNode;
  isPage?: boolean;
}

const QueriesComponent = <Q extends Queries = Queries>({
  children,
  renderErrors = renderDefaultErrorsMessage,
  renderLoader = defaultRenderLoader,
  onSuccess,
  onError,
  queries,
  isPage
}: PropsQueries<Q>): any => {
  const isSuccess = Object.values(queries).every(value => value.isSuccess);
  const successRef = useRef(isSuccess);

  useEffect(() => {
    if (!successRef.current && isSuccess) {
      successRef.current = true;
      onSuccess?.(queries as any);
    }
  }, [isSuccess, onSuccess, queries]);

  if (
    Object.values(queries).some(
      value =>
        value.status === QueryStatus.Loading ||
        value.status === QueryStatus.Idle
    )
  ) {
    return renderLoader();
  }

  if (Object.values(queries).some(value => value.isError)) {
    const failedQueries = Object.values(queries).filter(v => v.isError);
    const errors = failedQueries.map(value => value.error);
    onError?.(queries as any);
    if (isPage && renderDefaultErrorsMessage === renderErrors) {
      return RenderDefaultPageErrorMessage(errors, () =>
        Promise.all(Object.values(failedQueries).map(q => q.refetch()))
      );
    } else {
      return renderErrors(errors, () =>
        Promise.all(Object.values(failedQueries).map(q => q.refetch()))
      );
    }
  }

  return children(queries as any);
};

export function WSQuery<Q extends Query = Query>(
  props: PropsQuery<Q>
): React.FunctionComponentElement<{}> {
  return (
    <ErrorBoundary>
      <QueryComponent {...props} />
    </ErrorBoundary>
  );
}

export function WSQueries<Q extends Queries = Queries>(
  props: PropsQueries<Q>
): React.FunctionComponentElement<{}> {
  return (
    <ErrorBoundary>
      <QueriesComponent {...props} />
    </ErrorBoundary>
  );
}
